/**
 * BacklogPage Component
 * AIDLC Administrative Backend
 *
 * Backlog management for user requests and application defects.
 * Built on Marcus Chen's backend (store, routes, service).
 */

import { useState, useEffect, useCallback } from 'react';
import { TriageQueue } from './TriageQueue';
import { useAgents } from '../../hooks/useAgents';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { BacklogItemDetail } from './BacklogItemDetail';
import type { BacklogItem, BacklogItemType, BacklogPriority, BacklogStatus, BacklogSummary } from '../../types/backlog';
import {
  getBacklogItems,
  getBacklogSummary,
  createBacklogItem,
  updateBacklogItem,
  deleteBacklogItem,
  dispatchBacklogItem,
  stopBacklogItem,
  bulkDispatchBacklogItems,
  bulkStopBacklogItems,
} from '../../services/backlogService';
import type { Feature } from '../../types/feature';
import { getFeatures } from '../../services/featureService';

const priorityColors: Record<string, { variant: 'danger' | 'warning' | 'info' | 'neutral' }> = {
  critical: { variant: 'danger' },
  high: { variant: 'warning' },
  medium: { variant: 'info' },
  low: { variant: 'neutral' },
};

const statusLabels: Record<string, string> = {
  new: 'New',
  open: 'Open',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
  closed: 'Closed',
};

const statusVariants: Record<string, 'default' | 'info' | 'warning' | 'success' | 'neutral' | 'danger'> = {
  new: 'default',
  open: 'info',
  in_progress: 'warning',
  review: 'info',
  done: 'success',
  closed: 'neutral',
};

export function BacklogPage() {
  const { agents } = useAgents(0);
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [summary, setSummary] = useState<BacklogSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterProject, setFilterProject] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Detail modal
  const [selectedItem, setSelectedItem] = useState<BacklogItem | null>(null);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newType, setNewType] = useState<BacklogItemType>('user_request');
  const [newPriority, setNewPriority] = useState<BacklogPriority>('medium');
  const [newAgent, setNewAgent] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isDispatching, setIsDispatching] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // Features for column display
  const [features, setFeatures] = useState<Feature[]>([]);

  const loadData = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (filterProject) params.project = filterProject;
      if (filterType) params.type = filterType;
      if (filterPriority) params.priority = filterPriority;
      if (filterStatus) params.status = filterStatus;
      if (searchQuery) params.search = searchQuery;

      const [itemsData, summaryData, featuresData] = await Promise.all([
        getBacklogItems(params),
        getBacklogSummary(),
        getFeatures(),
      ]);
      setItems(itemsData);
      setSummary(summaryData);
      setFeatures(featuresData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load backlog');
    } finally {
      setIsLoading(false);
    }
  }, [filterProject, filterType, filterPriority, filterStatus, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setIsCreating(true);
    try {
      await createBacklogItem({
        title: newTitle.trim(),
        description: newDesc.trim(),
        type: newType,
        priority: newPriority,
        assignedAgent: newAgent || null,
      });
      setShowCreateModal(false);
      setNewTitle('');
      setNewDesc('');
      setNewType('user_request');
      setNewPriority('medium');
      setNewAgent('');
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create item');
    } finally {
      setIsCreating(false);
    }
  };

  const handleStatusChange = async (id: string, status: BacklogStatus) => {
    try {
      const updated = await updateBacklogItem(id, { status });
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update item');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBacklogItem(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete item');
    }
  };

  const toggleSelect = (itemId: string) => {
    setSelectedIds(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => prev.length === items.length ? [] : items.map(i => i.id));
  };

  const handleDispatch = async (id: string) => {
    try {
      await dispatchBacklogItem(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dispatch item');
    }
  };

  const handleStop = async (id: string) => {
    try {
      await stopBacklogItem(id);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop item');
    }
  };

  const handleBulkDispatch = async () => {
    if (selectedIds.length === 0) return;
    setIsDispatching(true);
    try {
      const result = await bulkDispatchBacklogItems(selectedIds);
      if (result.failed > 0) {
        const errors = result.results.filter(r => !r.success).map(r => `${r.itemId}: ${r.error}`);
        setError(`${result.dispatched} dispatched, ${result.failed} failed: ${errors.join('; ')}`);
      }
      setSelectedIds([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk dispatch failed');
    } finally {
      setIsDispatching(false);
    }
  };

  const handleBulkStop = async () => {
    if (selectedIds.length === 0) return;
    setIsStopping(true);
    try {
      await bulkStopBacklogItems(selectedIds);
      setSelectedIds([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk stop failed');
    } finally {
      setIsStopping(false);
    }
  };

  const formatDate = (ts: string) => {
    return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getAgentName = (agentId: string | null) => {
    if (!agentId) return 'Unassigned';
    const agent = agents.find((a) => a.id === agentId);
    return agent?.name || agentId;
  };

  if (isLoading) return <LoadingSpinner message="Loading backlog..." />;

  return (
    <div>
      <TriageQueue />

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-ldaja-black">{summary.total}</div>
            <div className="text-[10px] text-gray-500">Total Items</div>
          </div>
          <div className="bg-white border border-red-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-red-600">{summary.byPriority.critical}</div>
            <div className="text-[10px] text-gray-500">Critical</div>
          </div>
          <div className="bg-white border border-yellow-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-yellow-600">{summary.byStatus.new + summary.byStatus.open}</div>
            <div className="text-[10px] text-gray-500">New / Open</div>
          </div>
          <div className="bg-white border border-blue-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-ldaja-blue">{summary.byStatus.in_progress}</div>
            <div className="text-[10px] text-gray-500">In Progress</div>
          </div>
          <div className="bg-white border border-green-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-green-600">{summary.byStatus.done + summary.byStatus.closed}</div>
            <div className="text-[10px] text-gray-500">Completed</div>
          </div>
          <div className="bg-white border border-purple-200 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-purple-600">{summary.byType.defect}</div>
            <div className="text-[10px] text-gray-500">Defects</div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search backlog..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
          />
        </div>

        {/* Filters */}
        <select
          value={filterProject}
          onChange={(e) => setFilterProject(e.target.value)}
          className="text-xs border border-gray-200 rounded-md px-2 py-2 focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
        >
          <option value="">All Projects</option>
          <option value="hipaa-center">HIPAA Center</option>
          <option value="aidlc-admin">AIDLC Admin</option>
          <option value="aidlc_admin">AIDLC Admin (legacy)</option>
          <option value="rink_rater">Rink Rater</option>
          <option value="ldaja_website">LDAJA Website</option>
        </select>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="text-xs border border-gray-200 rounded-md px-2 py-2 focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
        >
          <option value="">All Types</option>
          <option value="user_request">User Request</option>
          <option value="defect">Defect</option>
        </select>

        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="text-xs border border-gray-200 rounded-md px-2 py-2 focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
        >
          <option value="">All Priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="text-xs border border-gray-200 rounded-md px-2 py-2 focus:outline-none focus:ring-1 focus:ring-ldaja-blue/50"
        >
          <option value="">All Statuses</option>
          <option value="new">New</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="review">Review</option>
          <option value="done">Done</option>
          <option value="closed">Closed</option>
        </select>

        <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M12 4v16m8-8H4" />
          </svg>
          New Item
        </Button>
      </div>

      {/* Bulk Action Toolbar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 mb-3 px-4 py-2.5 bg-ldaja-blue/5 border border-ldaja-blue/20 rounded-lg">
          <span className="text-xs font-medium text-ldaja-blue">{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
          <div className="flex-1" />
          <Button variant="primary" size="sm" onClick={handleBulkDispatch} isLoading={isDispatching} disabled={isDispatching || isStopping}>
            <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Start Selected
          </Button>
          <Button variant="secondary" size="sm" onClick={handleBulkStop} isLoading={isStopping} disabled={isDispatching || isStopping}>
            <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            Stop Selected
          </Button>
          <button onClick={() => setSelectedIds([])} className="text-xs text-gray-500 hover:text-gray-700 transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* Backlog Table */}
      {items.length === 0 ? (
        <EmptyState
          title="No backlog items"
          description="Create a new item to track user requests or defects."
        />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === items.length && items.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50"
                  />
                </th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">ID</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Title</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Project</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Priority</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">MoSCoW</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Assigned</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Feature</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Created</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((item) => (
                <tr key={item.id} className={`hover:bg-gray-50 cursor-pointer ${item.status === 'in_progress' ? 'bg-green-50/20 border-l-2 border-l-green-400' : ''}`} onClick={() => setSelectedItem(item)}>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="rounded border-gray-300 text-ldaja-blue focus:ring-ldaja-blue/50"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-[10px] font-mono text-gray-400">{item.id}</td>
                  <td className="px-4 py-2.5">
                    <div className="text-xs font-medium text-ldaja-blue max-w-[300px] truncate hover:underline">{item.title}</div>
                    {item.labels.length > 0 && (
                      <div className="flex gap-1 mt-0.5">
                        {item.labels.map((l) => (
                          <span key={l} className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{l}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                      {item.project === 'aidlc-admin' || item.project === 'aidlc_admin' ? 'AIDLC' :
                       item.project === 'hipaa-center' ? 'HIPAA Center' :
                       item.project === 'rink_rater' ? 'Rink Rater' :
                       item.project === 'ldaja_website' ? 'Website' :
                       item.project || '--'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      item.type === 'defect' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                    }`}>
                      {item.type === 'defect' ? 'Defect' : 'Request'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant={priorityColors[item.priority]?.variant || 'neutral'}>
                      {item.priority}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    {item.moscow ? (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        item.moscow === 'must' ? 'bg-red-50 text-red-700' :
                        item.moscow === 'should' ? 'bg-orange-50 text-orange-700' :
                        item.moscow === 'could' ? 'bg-blue-50 text-blue-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {item.moscow === 'must' ? 'Must' : item.moscow === 'should' ? 'Should' : item.moscow === 'could' ? 'Could' : "Won't"}
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-300">--</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant={statusVariants[item.status] || 'neutral'} dot>
                      {statusLabels[item.status] || item.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-600">
                    {getAgentName(item.assignedAgent)}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {item.featureId
                      ? (features.find(f => f.id === item.featureId)?.title || item.featureId)
                      : <span className="text-gray-300">--</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {formatDate(item.createdAt)}
                  </td>
                  <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      {item.status === 'new' && (
                        <button
                          onClick={() => handleStatusChange(item.id, 'open')}
                          className="text-[10px] px-2 py-1 bg-ldaja-blue text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Open
                        </button>
                      )}
                      {item.status === 'open' && (
                        <button
                          onClick={() => item.assignedAgent ? handleDispatch(item.id) : handleStatusChange(item.id, 'in_progress')}
                          className={`text-[10px] px-2 py-1 rounded transition-colors ${
                            item.assignedAgent
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-yellow-500 text-white hover:bg-yellow-600'
                          }`}
                          title={item.assignedAgent ? `Dispatch to ${getAgentName(item.assignedAgent)}` : 'Start (no agent assigned)'}
                        >
                          {item.assignedAgent ? 'Dispatch' : 'Start'}
                        </button>
                      )}
                      {item.status === 'in_progress' && (
                        <>
                          <button
                            onClick={() => handleStop(item.id)}
                            className="text-[10px] px-2 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors"
                          >
                            Stop
                          </button>
                          <button
                            onClick={() => handleStatusChange(item.id, 'review')}
                            className="text-[10px] px-2 py-1 bg-ldaja-blue text-white rounded hover:bg-blue-700 transition-colors"
                          >
                            Review
                          </button>
                        </>
                      )}
                      {item.status === 'review' && (
                        <button
                          onClick={() => handleStatusChange(item.id, 'done')}
                          className="text-[10px] px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                        >
                          Done
                        </button>
                      )}
                      {(item.status === 'done') && (
                        <button
                          onClick={() => handleStatusChange(item.id, 'closed')}
                          className="text-[10px] px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                        >
                          Close
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-[10px] px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors"
                      >
                        Del
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedItem && (
        <BacklogItemDetail
          item={selectedItem}
          isOpen={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdated={() => { loadData(); setSelectedItem(null); }}
        />
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="New Backlog Item"
        size="md"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleCreate} isLoading={isCreating} disabled={!newTitle.trim()}>
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="Brief description of the request or defect..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              placeholder="Detailed description, steps to reproduce, expected behavior..."
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as BacklogItemType)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              >
                <option value="user_request">User Request</option>
                <option value="defect">Defect</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value as BacklogPriority)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Assign To</label>
              <select
                value={newAgent}
                onChange={(e) => setNewAgent(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/50"
              >
                <option value="">Unassigned</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
